import React, { useEffect, useState } from 'react'
import { Autocomplete, FormControl, TextField } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import { authFetch } from '~/services/fetch'

interface WorkScheduleOption {
  readonly id: number
  readonly name: string
}

interface FilterAutocompleteWorkScheduleProps {
  readonly onWorkScheduleChange: (value: number | null) => void
  readonly workScheduleId?: number | null
  readonly disabled?: boolean
  readonly label?: string
}

async function getWorkSchedules(): Promise<readonly WorkScheduleOption[]> {
  try {
    const response = await authFetch({
      url: '/work-schedule',
      method: 'POST',
      data: { without_pagination: true },
    })
    const data: readonly any[] = response.data?.data ?? response.data ?? []
    return data.map((item) => ({ id: item.id, name: item.name ?? String(item.id) }))
  } catch {
    return []
  }
}

export default function FilterAutocompleteWorkSchedule({
  disabled,
  onWorkScheduleChange,
  workScheduleId,
  label,
}: FilterAutocompleteWorkScheduleProps) {
  const { translate } = useLang()
  const [options, setOptions] = useState<readonly WorkScheduleOption[]>([])
  const [value, setValue] = useState<WorkScheduleOption | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    getWorkSchedules()
      .then((schedules) => {
        setOptions(schedules)
        setValue(null)
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!workScheduleId) {
      setValue(null)
    } else {
      setValue(options.find((item) => item.id === workScheduleId) ?? null)
    }
  }, [workScheduleId, options])

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        id="filter-work-schedule"
        options={options}
        disabled={disabled}
        value={value}
        noOptionsText={translate('No options')}
        clearText={translate('Clear')}
        size="small"
        loading={loading}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.name}
        onChange={(_, selected) => {
          setValue(selected)
          onWorkScheduleChange(selected?.id ?? null)
        }}
        renderInput={(params) => (
          <TextField {...params} label={translate(label ?? 'Scale')} />
        )}
      />
    </FormControl>
  )
}
